// src/components/auth/ResetPassword.js

import React, { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { postData } from "../../services/FetchNodeservices";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import LoginAnimate from "../../Animation/LoginAnimate.json";

function PasswordInput({ label, value, setValue, show, setShow }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">{label}</label>

      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={label}
          className="w-full px-4 py-3 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition duration-200 text-sm font-medium"
        />

        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600 transition"
        >
          {show ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();

  const [employeeCode, setEmployeeCode] = useState("");
  const [userName, setUserName] = useState("");
  const [step, setStep] = useState(1);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [message, setMessage] = useState("");

  const handleCheckEmployee = async () => {
    try {
      const res = await postData("reset-password/request", {
        employee_code: employeeCode,
      });

      if (res.success) {
        setUserName(`${res.first_name} ${res.last_name}`);
        setStep(2);
        setMessage("");
      } else {
        setMessage(res.message || "Invalid employee code");
      }
    } catch (err) {
      console.error(err);
      setMessage("Server error, please try again.");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword)
      return setMessage("Enter both password fields");

    if (newPassword !== confirmPassword)
      return setMessage("Passwords do not match");
    
    const res = await postData("reset-password/confirm", {
      employee_code: employeeCode,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });

    setMessage(res.message);

    if (res.success) {
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-tr from-indigo-50 via-white to-purple-50 p-4 relative overflow-hidden font-sans">
      {/* Background blur decorative circles */}
      <div className="absolute -top-[10%] -left-[10%] w-[35rem] h-[35rem] rounded-full bg-indigo-200/30 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-[10%] -right-[10%] w-[35rem] h-[35rem] rounded-full bg-purple-200/30 blur-[120px] pointer-events-none" />

      <div className="bg-white border border-gray-100 shadow-2xl shadow-indigo-100/40 rounded-[32px] w-full max-w-4xl overflow-hidden grid grid-cols-1 md:grid-cols-2 relative z-10 animate-fadeIn">
        
        {/* LEFT - FORM */}
        <div className="flex flex-col justify-center p-8 md:p-14">
          <div className="mb-8 text-center md:text-left">
            <div 
              onClick={() => navigate("/login")}
              className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white font-black text-xl shadow-lg shadow-indigo-200 mb-4 mx-auto md:mx-0 cursor-pointer hover:scale-105 transition"
            >
              M
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
              Reset Password
            </h2>
            <p className="text-xs md:text-sm font-semibold text-gray-400 mt-1">
              Securely update your portal access key
            </p>
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Employee Code</label>
                <input
                  type="text"
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                  placeholder="Enter employee code (e.g. MPC1801)"
                  className="w-full px-4 py-3 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition duration-200 text-sm font-medium"
                />
              </div>

              <button
                onClick={handleCheckEmployee}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-2xl shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 active:scale-[0.99]"
              >
                Verify Employee Code
              </button>

              <div className="text-center mt-4">
                <span 
                  onClick={() => navigate("/login")}
                  className="text-xs text-indigo-600 font-extrabold cursor-pointer hover:text-indigo-800 transition"
                >
                  Back to Sign In
                </span>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-5">
              <p className="text-xs font-semibold text-gray-500 text-center md:text-left bg-indigo-50/50 border border-indigo-100 rounded-2xl p-3">
                Hi <span className="font-bold text-indigo-700">{userName}</span>, please configure your new secure password below.
              </p>

              <PasswordInput
                label="New Password"
                value={newPassword}
                setValue={setNewPassword}
                show={showPassword}
                setShow={setShowPassword}
              />

              <PasswordInput
                label="Confirm Password"
                value={confirmPassword}
                setValue={setConfirmPassword}
                show={showConfirm}
                setShow={setShowConfirm}
              />

              <button
                onClick={handleResetPassword}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-2xl shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 active:scale-[0.99]"
              >
                Reset Password
              </button>
            </div>
          )}

          {/* Message */}
          {message && (
            <div
              className={`mt-5 text-center text-xs p-3 rounded-2xl font-bold ${
                message.toLowerCase().includes("success")
                  ? "bg-green-50 text-green-700 border border-green-100"
                  : "bg-red-50 text-red-600 border border-red-100"
              }`}
            >
              {message}
            </div>
          )}
        </div>

        {/* RIGHT - ANIMATION */}
        <div className="hidden md:flex items-center justify-center bg-[#f4f6fa]/40 p-8 border-l border-gray-50">
          <Lottie animationData={LoginAnimate} loop className="w-full max-w-[360px]" />
        </div>
      </div>
    </div>
  );
}
